import React from 'react';
import { DELIVERY_STATUSES } from '../../../support/campaigns';
import { formatPipelineUsd, type PipelineMetrics } from '../../../support/campaignLeader';
import { DASH } from './CampaignChartCard';

/**
 * Pipeline **relative to delivery status** for a leader's team subtree (Req 5, team-hierarchy side):
 * one row per delivery status showing how many sessions are in it, how many of those have no
 * opportunity attached yet (so still $0 attributed), and the linked opportunity $. Every session is
 * counted regardless of whether an opportunity is attached — session volume is the leading indicator,
 * the $ is context. Styled to match {@link CampaignChartCard}.
 */
interface Props {
  metrics: PipelineMetrics;
  title?: string;
  subtitle?: string;
}

const PipelineStatusTable: React.FC<Props> = ({
  metrics,
  title = 'Pipeline by delivery status',
  subtitle = 'Sessions + attributed $ per status (incl. no-opportunity sessions)',
}) => (
  <div
    style={{
      background: DASH.surface,
      border: `1px solid ${DASH.border}`,
      borderRadius: DASH.radius,
      boxShadow: DASH.shadowCard,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      overflow: 'hidden',
    }}
  >
    <div style={{ padding: '14px 18px', borderBottom: `1px solid ${DASH.border}` }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: DASH.text }}>{title}</div>
      {subtitle ? (
        <div style={{ fontSize: '0.75rem', color: DASH.textDim, marginTop: 2 }}>{subtitle}</div>
      ) : null}
    </div>
    <div style={{ padding: '12px 14px' }}>
    <table
      className="w-full text-sm"
      style={{ borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}
    >
      <thead>
        <tr className="hbr-css__text-secondary-xs">
          <th style={{ textAlign: 'left', padding: '6px 8px' }}>Status</th>
          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Sessions</th>
          <th style={{ textAlign: 'right', padding: '6px 8px' }}>No opp</th>
          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Attributed $</th>
        </tr>
      </thead>
      <tbody>
        {DELIVERY_STATUSES.map((s) => (
          <tr key={s.value} style={{ borderTop: '1px solid #f0f0f0' }}>
            <td style={{ padding: '6px 8px' }}>{s.label}</td>
            <td style={{ textAlign: 'right', padding: '6px 8px' }}>{metrics.countByStatus[s.value]}</td>
            <td style={{ textAlign: 'right', padding: '6px 8px' }}>{metrics.noOppByStatus[s.value]}</td>
            <td style={{ textAlign: 'right', padding: '6px 8px' }}>{formatPipelineUsd(metrics.byStatus[s.value])}</td>
          </tr>
        ))}
        <tr style={{ borderTop: '1px solid #d0d0d0', fontWeight: 600 }}>
          <td style={{ padding: '6px 8px' }}>Total</td>
          <td style={{ textAlign: 'right', padding: '6px 8px' }}>{metrics.totalCount}</td>
          <td style={{ textAlign: 'right', padding: '6px 8px' }}>
            {DELIVERY_STATUSES.reduce((n, s) => n + metrics.noOppByStatus[s.value], 0)}
          </td>
          <td style={{ textAlign: 'right', padding: '6px 8px' }}>{formatPipelineUsd(metrics.total)}</td>
        </tr>
      </tbody>
    </table>
    </div>
  </div>
);

export default PipelineStatusTable;
